import { useEffect, useMemo, useState } from 'react'

import {
	EuiButton,
	EuiCallOut,
	EuiDescriptionList,
	EuiDescriptionListDescription,
	EuiDescriptionListTitle,
	EuiFieldNumber,
	EuiFieldText,
	EuiFlexGroup,
	EuiFlexItem,
	EuiFormRow,
	EuiHorizontalRule,
	EuiModalBody,
	EuiModalFooter,
	EuiPanel,
	EuiProgress,
	EuiSpacer,
	EuiText,
	EuiTitle,
} from '@equipmentshare/ds2'

import CurrencyField from 'components/CurrencyField/currencyField'
import ReasonSelect from './components/ReasonSelect'
import PurchaseOrderSelect from './components/PurchaseOrderSelect'
import VendorSelect from './components/VendorSelect'
import AcquisitionMethodSelect from './components/AcquisitionMethodSelect'
import { heapEvent } from '@equipmentshare/heaper'
import {
	IncrementOrDecrement,
	ManualAdjustmentReasonsQuery,
	ManualAdjustmentReasonType,
	useStorePartDetailsQuery,
} from '../../../../graphql'

// Extract the actual ManualAdjustmentReason type returned by the query
type ManualAdjustmentReasonNode = ManualAdjustmentReasonsQuery['manualAdjustmentReasons'][number]
import { useCcPurchaseQuery } from '../../../../graphql/costcapture'
import validateForm, { checkForUnexpectedCost, SetUnexpectedCost } from './helpers/validateForm'
import useAdjustItem from '../../effects/adjustItem'
import usePurchaseOrderValidation from './hooks/usePurchaseOrderValidation'
import { GRAPHQL_ENDPOINTS } from 'config/apollo-client'

export type AdjustQuantityFormState = {
	quantity: string,
	cost: string,
	reason?: ManualAdjustmentReasonNode,
	memo: string,
	// New vendor-related fields
	vendorId?: string,
	purchaseOrderId?: string,
	// new credit card workflow fields
	purchaseId?: string,
	vendorName?: string,
	acquisitionMethod?: 'purchase_order' | 'direct_vendor' | 'credit_card_purchase',
	validation: {
		quantity: { isInvalid: boolean, error?: string },
		cost: { isInvalid: boolean, error?: string },
		reason: { isInvalid: boolean, error?: string },
		// New validation fields
		vendor: { isInvalid: boolean, error?: string },
		purchaseOrder: { isInvalid: boolean, error?: string },
		purchaseId?: { isInvalid: boolean, error?: string },
	},
}

export type AdjustQuantityProps = {
	storePartId: number,
	onCancel: () => void,
	onSuccess: () => void,
}

export function getReasonDirection( availableQuantity: number, updatedQuantityString: string ): IncrementOrDecrement | undefined {
	const updatedQuantity = parseInt( updatedQuantityString, 10 )

	if ( !isNaN( updatedQuantity ) ) {
		if ( updatedQuantity < availableQuantity ) {
			return IncrementOrDecrement.Decrement
		} else {
			return IncrementOrDecrement.Increment
		}
	}

	return undefined
}

export default function AdjustQuantity( { storePartId, onCancel, onSuccess }: AdjustQuantityProps ) {
	const { data, loading } = useStorePartDetailsQuery( { variables: { storePartId } } )
	const [ formState, setFormState ] = useState<AdjustQuantityFormState>( {
		quantity: '',
		cost: '',
		memo: '',
		// Initialize new vendor fields
		vendorId: undefined,
		purchaseOrderId: undefined,
		acquisitionMethod: undefined,
		validation: {
			quantity: { isInvalid: false },
			cost: { isInvalid: false },
			reason: { isInvalid: false },
			// Initialize new validation fields
			vendor: { isInvalid: false },
			purchaseOrder: { isInvalid: false },
		}
	} )

	const {
		data: ccPurchaseData,
		loading: ccLoading,
	} = useCcPurchaseQuery( {
		variables: { id: formState.purchaseId ?? '' },
		skip: !formState.purchaseId,
		context: { endpoint: GRAPHQL_ENDPOINTS.COST_CAPTURE },
	} )

	useEffect( () => {
		if (
			formState.acquisitionMethod !== 'credit_card_purchase' ||
			!formState.purchaseId
		) {
			return
		}

		// still loading → remain invalid
		if ( ccLoading ) {
			return
		}

		const purchase = ccPurchaseData?.purchase

		const resolvedVendorName =
			purchase?.vendor?.name ??
			purchase?.unofficialVendorName ??
			''
		const hasVendorIdentity = resolvedVendorName !== ''

		// VALID CASE
		if ( hasVendorIdentity ) {
			setFormState( {
				...formState,
				vendorName: resolvedVendorName,
				validation: {
					...formState.validation,
					purchaseId: { isInvalid: false },
				},
			} )
			return
		}

		// INVALID CASE
		setFormState( {
			...formState,
			vendorName: '',
			validation: {
				...formState.validation,
				purchaseId: {
					isInvalid: true,
					error: 'Purchase does not have a vendor',
				},
			},
		} )
	}, [
		formState.purchaseId,
		formState.acquisitionMethod,
		ccPurchaseData,
		ccLoading,
	] )

	const [ reasonDir, setReasonDir ] = useState<IncrementOrDecrement>()
	const [ isSaving, setIsSaving ] = useState( false )
	const [ unexpectedCost, setUnexpectedCostFlag ] = useState<'high' | 'low'>()
	const adjustItem = useAdjustItem()

	const loadedDateTime = useMemo( () => {
		const dateTime = Date.now()
		const formatter = new Intl.DateTimeFormat( 'default', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		} )

		return formatter.format( dateTime )
	}, [ data ] )
	const storePart = useMemo( () => data?.storePart, [ data ] )
	const availableQuantity = useMemo( () => storePart?.availableQuantity ?? 0, [ storePart ] )
	const storeId = storePart?.inventoryLocation?.id ? parseInt( storePart.inventoryLocation.id, 10 ) : undefined
	const branchId = storePart?.inventoryLocation?.branchId

	// Detect when "Return to Vendor" reason is selected
	const isReturnToVendor = useMemo( () => {
		return formState.reason?.reason === ManualAdjustmentReasonType.ReturnToVendor
	}, [ formState.reason ] )

	// Validate purchase order contains current part
	const poValidation = usePurchaseOrderValidation( {
		purchaseOrderId: formState.purchaseOrderId,
		currentPartItemId: storePart?.part.itemId,
		enabled: isReturnToVendor && formState.acquisitionMethod === 'purchase_order'
	} )

	useEffect( () => {
		if ( data?.storePart && data.storePart.cost ) {
			setFormState( {
				...formState,
				cost: data.storePart.cost.toString(),
				validation: {
					...formState.validation,
					cost: { isInvalid: false }
				}
			} )
		}
	}, [ data?.storePart ] )

	const qtyHelpText = useMemo( () => {
		if ( formState.quantity === '' || !storePart ) {
			return ''
		}
		const qty = parseInt( formState.quantity, 10 )
		const diff = Math.abs( availableQuantity - qty )

		if ( qty < availableQuantity ) {
			return `Decreases quantity by ${diff}`
		} else {
			return `Increases quantity by ${diff}`
		}
	}, [ formState.quantity ] )

	const onChangeQuantity = ( event: React.ChangeEvent<HTMLInputElement> ) => {
		const quantity = event.currentTarget.value
		setFormState( {
			...formState,
			quantity,
			validation: {
				...formState.validation,
				quantity: { isInvalid: false },
			}
		} )
		setReasonDir( getReasonDirection( availableQuantity, quantity ) )
	}

	const onChangeCost = ( cost: string, triggerValidation?: boolean ) => {
		const newFormState = {
			...formState,
			cost,
		}

		if ( triggerValidation ) {
			newFormState.validation = {
				...newFormState.validation,
				cost: { isInvalid: false },
			}
		}
		setFormState( newFormState )
	}

	const onChangeReason = ( _: any, reason?: ManualAdjustmentReasonNode ) => {
		setFormState( {
			...formState,
			reason,
			// Always reset vendor fields when reason changes
			vendorId: undefined,
			purchaseOrderId: undefined,
			acquisitionMethod: undefined,
			vendorName: '',
			validation: {
				...formState.validation,
				reason: { isInvalid: false },
				quantity: { isInvalid: false },
				// Reset vendor validation when reason changes
				vendor: { isInvalid: false },
				purchaseOrder: { isInvalid: false },
				purchaseId: { isInvalid: true }
			}
		} )
	}

	const onChangeMemo = ( event: React.ChangeEvent<HTMLInputElement> ) => setFormState( {
		...formState,
		memo: event.currentTarget.value,
	} )

	// Handler for acquisition method selection
	const onChangeAcquisitionMethod = (
		acquisitionMethod: 'purchase_order' | 'direct_vendor' | 'credit_card_purchase'
	) => {
		setFormState( {
			...formState,
			acquisitionMethod,

			// Reset all acquisition-related fields
			vendorId: undefined,
			purchaseOrderId: undefined,
			vendorName: undefined,

			validation: {
				...formState.validation,
				vendor: { isInvalid: false },
				purchaseOrder: { isInvalid: false },
			},
		} )
	}

	// Handler for purchase order selection
	const onChangePurchaseOrder = ( selection: { purchaseOrderId: string, vendorId: string } | undefined ) => {
		setFormState( {
			...formState,
			purchaseOrderId: selection?.purchaseOrderId,
			vendorId: selection?.vendorId,
			validation: {
				...formState.validation,
				vendor: { isInvalid: false },
				purchaseOrder: { isInvalid: false },
			}
		} )
	}

	// Handler for direct vendor selection
	const onChangeVendor = ( vendorId: string | undefined ) => {
		setFormState( {
			...formState,
			vendorId,
			validation: {
				...formState.validation,
				vendor: { isInvalid: false },
			}
		} )
	}

	const onChangePurchaseId = ( purchaseId: string ) => {
		setFormState( {
			...formState,
			purchaseId,
			vendorName: undefined, // clear derived data
			validation: {
				...formState.validation,
				purchaseId: {
					isInvalid: true, // disable until we KNOW we have a valid CC transaction
				}
			},
		} )
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Adjust Quantity Modal - Click - Cancel', {} )
		onCancel()
	}

	// Updated handleSubmit payload
	const handleSubmit = async() => {
		if ( !unexpectedCost ) {
			heapEvent( 'Inventory - Adjust Quantity Modal - Click - Adjust Quantity', {} )
			setIsSaving( true )
			const { isInvalid, newFormState } = validateForm( formState, availableQuantity )

			if ( isInvalid ) {
				setIsSaving( false )
				setFormState( newFormState )
			} else {
				const quantity = Math.abs( availableQuantity - parseInt( formState.quantity, 10 ) )

				await adjustItem(
					// data is already validated at this point, just making typescript happy
					( storePart?.inventoryLocation?.id ?? 0 ).toString(),
					( storePart?.part.id ?? 0 ).toString(),
					quantity,
					parseFloat( formState.cost ),
					formState.reason?.reason as ManualAdjustmentReasonType,
					reasonDir ?? IncrementOrDecrement.Increment,
					formState.memo,
					formState.vendorId,
					formState.purchaseOrderId,
					formState.purchaseId,
				)
				onSuccess()
			}
		}
	}

	const setUnexpectedCost: SetUnexpectedCost = ( flag ) => {
		if ( flag === 'normal' ) {
			setUnexpectedCostFlag( undefined )
		} else {
			setUnexpectedCostFlag( flag )
		}
	}

	const onBlurCost = () => {
		const costValidation = checkForUnexpectedCost( formState, storePart?.cost ?? undefined, setUnexpectedCost )
		setFormState( {
			...formState,
			validation: {
				...formState.validation,
				cost: costValidation,
			}
		} )
	}

	const ignoreUnexpectedCost = () => {
		setUnexpectedCostFlag( undefined )
		setFormState( {
			...formState,
			validation: {
				...formState.validation,
				cost: { isInvalid: false },
			}
		} )
	}

	return (
		<>
			<EuiModalBody>
				{ loading && (
					<EuiProgress />
				) }
				{ unexpectedCost && (
					<>
						<EuiCallOut
							color="danger"
							title="Price Adjustment"
						>
							The price entered is {unexpectedCost === 'high' ? 'higher' : 'lower'} than our recorded price. If this is correct, click &lsquo;Ignore&rsquo; below to continue. Otherwise, adjust accordingly.
							<EuiSpacer size="m" />
							<EuiButton
								onClick={ignoreUnexpectedCost}
								color="danger"
								id="ignoreUnexpectedCost"
							>
								Ignore
							</EuiButton>
						</EuiCallOut>
						<EuiSpacer size="m" />
					</>
				) }
				<EuiDescriptionList compressed>
					<EuiDescriptionListTitle>
						Store
					</EuiDescriptionListTitle>
					<EuiDescriptionListDescription>
						{storePart?.inventoryLocation?.name}
					</EuiDescriptionListDescription>
					<EuiDescriptionListTitle>
						Item Description
					</EuiDescriptionListTitle>
					<EuiDescriptionListDescription>
						{storePart?.part.partClass.description}
					</EuiDescriptionListDescription>
					<EuiDescriptionListTitle>
						Manufacturer Number
					</EuiDescriptionListTitle>
					<EuiDescriptionListDescription>
						{storePart?.part.partNumber}
					</EuiDescriptionListDescription>
				</EuiDescriptionList>
				<EuiHorizontalRule margin="m" />

				<EuiFlexGroup>
					<EuiFlexItem>
						<EuiDescriptionList compressed>
							<EuiDescriptionListTitle>
								On hand qty
							</EuiDescriptionListTitle>
							<EuiDescriptionListDescription>
								{storePart?.availableQuantity}
							</EuiDescriptionListDescription>
						</EuiDescriptionList>
					</EuiFlexItem>
					<EuiFlexItem>
						<EuiDescriptionList compressed>
							<EuiDescriptionListTitle>
								Allocated qty
							</EuiDescriptionListTitle>
							<EuiDescriptionListDescription>
								{( storePart?.quantity ?? 0 ) - availableQuantity }
							</EuiDescriptionListDescription>
						</EuiDescriptionList>
					</EuiFlexItem>
				</EuiFlexGroup>
				<EuiText size="xs">As of {loadedDateTime}</EuiText>
				<EuiHorizontalRule margin="m" />

				<EuiFlexGroup>
					<EuiFlexItem grow={false}>
						<EuiFormRow
							id="quantityRow"
							label={<>Updated on hand qty<span className="required">*</span></>}
							helpText={qtyHelpText}
							isInvalid={formState.validation.quantity.isInvalid}
							error={formState.validation.quantity.error}
							style={{ width: '180px' }}
						>
							<EuiFieldNumber
								id="quantityField"
								onChange={onChangeQuantity}
								value={formState.quantity}
								isInvalid={formState.validation.quantity.isInvalid}
							/>
						</EuiFormRow>
					</EuiFlexItem>

					<EuiFlexItem grow={false}>
						<EuiFormRow
							label={<>Cost per item<span className="required">*</span></>}
							isInvalid={formState.validation.cost.isInvalid}
							error={formState.validation.cost.error}
							style={{ width: '180px' }}
						>
							<CurrencyField
								onRawChange={onChangeCost}
								onBlur={onBlurCost}
								value={formState.cost}
								isInvalid={formState.validation.cost.isInvalid}
							/>
						</EuiFormRow>
					</EuiFlexItem>
				</EuiFlexGroup>

				<EuiSpacer size="xxl" style={{
					blockSize: qtyHelpText === '' ? '40px' : '20px'
				}} />

				<EuiFormRow
					label={<>Reason<span className="required">*</span></>}
					isInvalid={formState.validation.reason.isInvalid}
					error={formState.validation.reason.error}
					style={{ width: '240px' }}
				>
					<ReasonSelect
						selectedReason={formState.reason?.reason as ManualAdjustmentReasonType}
						onChange={onChangeReason}
						reasonType={reasonDir}
						isInvalid={formState.validation.reason.isInvalid}
						storeId={storeId}
						branchId={branchId}
					/>
				</EuiFormRow>

				{/* Vendor Information Section - shown only when "Return to Vendor" is selected */}
				{isReturnToVendor && (
					<>
						<EuiSpacer size="l" />
						<EuiPanel paddingSize="m" hasShadow={false} hasBorder>
							<EuiTitle size="xs">
								<h3>Vendor Information</h3>
							</EuiTitle>
							<EuiSpacer size="m" />

							<EuiFormRow
								label="How was this product acquired?"
								isInvalid={formState.validation.vendor.isInvalid && !formState.acquisitionMethod}
								error={!formState.acquisitionMethod ? formState.validation.vendor.error : undefined}
							>
								<AcquisitionMethodSelect
									value={formState.acquisitionMethod}
									onChange={onChangeAcquisitionMethod}
								/>
							</EuiFormRow>

							<EuiSpacer size="m" />

							{formState.acquisitionMethod === 'purchase_order' && (
								<>
									<EuiFormRow
										label={<>Purchase Order<span className="required">*</span></>}
										isInvalid={formState.validation.purchaseOrder.isInvalid}
										error={formState.validation.purchaseOrder.error}
										style={{ width: '400px' }}
									>
										<PurchaseOrderSelect
											value={formState.purchaseOrderId && formState.vendorId ? {
												purchaseOrderId: formState.purchaseOrderId,
												vendorId: formState.vendorId
											} : undefined}
											onChange={onChangePurchaseOrder}
											isInvalid={formState.validation.purchaseOrder.isInvalid}
										/>
									</EuiFormRow>

									{/* Purchase Order Validation Warning */}
									{poValidation.hasWarning && (
										<>
											<EuiSpacer size="m" />
											<EuiCallOut
												color="warning"
												size="s"
												title="Missing Product"
											>
												{poValidation.warningMessage}
											</EuiCallOut>
										</>
									)}
								</>
							)}

							{formState.acquisitionMethod === 'direct_vendor' && (
								<EuiFormRow
									label={<>Vendor<span className="required">*</span></>}
									isInvalid={formState.validation.vendor.isInvalid}
									error={formState.validation.vendor.error}
									style={{ width: '400px' }}
								>
									<VendorSelect
										value={formState.vendorId}
										onChange={onChangeVendor}
										isInvalid={formState.validation.vendor.isInvalid}
									/>
								</EuiFormRow>
							)}

							{formState.acquisitionMethod === 'credit_card_purchase' && (
								<>
									<EuiFormRow
										label={<>Purchase ID<span className="required">*</span></>}
										isInvalid={formState.validation.purchaseId?.isInvalid}
										error={formState.validation.purchaseId?.error}
										style={{ width: '400px' }}
									>
										<EuiFieldText
											placeholder="Enter Purchase ID"
											value={formState.purchaseId ?? ''}
											onChange={( e ) => onChangePurchaseId( e.target.value )}
											isLoading={ccLoading}
										/>
									</EuiFormRow>
									<EuiFormRow
										label="Vendor (auto-filled)"
										helpText="Vendor name from CostCapture"
									>
										<EuiFieldText value={formState.vendorName ?? ''} readOnly />
									</EuiFormRow>
								</>
							)}
						</EuiPanel>
						<EuiSpacer size="l" />
					</>
				)}
				<EuiFormRow
					label="Memo"
					style={{ width: '400px' }}
				>
					<EuiFieldText
						value={formState.memo}
						onChange={onChangeMemo}
					/>
				</EuiFormRow>
			</EuiModalBody>

			<EuiModalFooter>
				<EuiButton
					id="cancelBtn"
					color="text"
					onClick={handleCancel}
				>
					Cancel
				</EuiButton>
				<EuiButton
					disabled={formState.acquisitionMethod === 'credit_card_purchase' && formState.validation.purchaseId?.isInvalid}
					id="submitBtn"
					color="primary"
					fill
					isLoading={isSaving}
					onClick={handleSubmit}
				>
					Adjust quantity
				</EuiButton>
			</EuiModalFooter>
		</>
	)
}
